import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    useGetBingXWalletsQuery,
    useCreateBingXWalletMutation,
    useUpdateBingXWalletMutation,
    useDeleteBingXWalletMutation,
    useSyncBingXAddressesMutation,
    useLazyGetBingXBalanceQuery,
    useLazyGetBingXWithdrawalsQuery,
    useGetBingXTopupsQuery,
    useToggleBingXAddressMutation,
    useAddBingXAddressMutation,
    useResetBingXSumTotalMutation,
} from "../api";
import type { BingXAddress, BingXWallet } from "../api/bingx";
import { useNotification } from "../hooks/useNotification";

type MainTab = "wallets" | "topups";
type ModalTab = "main" | "addresses" | "bingx";
type Network = "bep20" | "trc20" | "ton";

const NETWORKS: { key: Network; label: string; chip: string; bar: string }[] = [
    { key: "bep20", label: "BEP20", chip: "bg-amber-100 text-amber-900", bar: "bg-amber-500" },
    { key: "trc20", label: "TRC20", chip: "bg-rose-100 text-rose-900", bar: "bg-rose-500" },
    { key: "ton", label: "TON", chip: "bg-sky-100 text-sky-900", bar: "bg-sky-500" },
];

const fmt = (n: number | null | undefined, dig = 2) =>
    n == null || Number.isNaN(n)
        ? "—"
        : Number(n).toLocaleString("ru-RU", { maximumFractionDigits: dig });

const fmtUsd = (n: number | null | undefined, dig = 2) =>
    n == null || Number.isNaN(n) ? "—" : `$ ${fmt(n, dig)}`;

const shortAddr = (a: string) =>
    !a ? "—" : a.length > 16 ? `${a.slice(0, 8)}…${a.slice(-6)}` : a;

const SourceBadge: React.FC<{ source?: string }> = ({ source }) =>
    source === "millennium" ? (
        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-100 text-violet-800">
            Millennium
        </span>
    ) : (
        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700">
            Хаб
        </span>
    );

const StatusBadge: React.FC<{ status: number }> = ({ status }) => {
    const map: Record<number, { t: string; c: string }> = {
        0: { t: "Ожидает", c: "bg-amber-100 text-amber-800" },
        1: { t: "Отправлено", c: "bg-blue-100 text-blue-800" },
        2: { t: "Оплачен", c: "bg-emerald-100 text-emerald-800" },
        3: { t: "Удалён", c: "bg-red-100 text-red-800" },
        4: { t: "Авто-отмена", c: "bg-red-100 text-red-800" },
        5: { t: "Таймаут", c: "bg-red-100 text-red-800" },
    };
    const m = map[status] ?? { t: `ст.${status}`, c: "bg-gray-100 text-gray-700" };
    return (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${m.c}`}>
            {m.t}
        </span>
    );
};

const CryptoWalletsPage: React.FC = () => {
    const { showNotification } = useNotification();
    const [mainTab, setMainTab] = useState<MainTab>("wallets");
    const [searchTerm, setSearchTerm] = useState("");

    const [modalOpen, setModalOpen] = useState(false);
    const [modalTab, setModalTab] = useState<ModalTab>("main");
    const [addrNet, setAddrNet] = useState<Network>("bep20");
    const [editing, setEditing] = useState<BingXWallet | null>(null);
    const [form, setForm] = useState({
        alny_bingx_accounts_account: "",
        alny_bingx_accounts_status: 0,
        api_key: "",
        secret_key: "",
    });
    const [newAddress, setNewAddress] = useState("");
    const [bingxLive, setBingxLive] = useState<{
        balance?: any;
        withdrawals?: any[];
        loading?: boolean;
    }>({});
    /** live баланс біржі на картках: id → { total, err, loading } */
    const [cardLive, setCardLive] = useState<
        Record<number, { total: number | null; err?: string; loading?: boolean }>
    >({});
    const [liveLoadingAll, setLiveLoadingAll] = useState(false);

    const [tpStatus, setTpStatus] = useState("all");
    const [tpNetwork, setTpNetwork] = useState("");
    const [tpSource, setTpSource] = useState("");
    const [tpUserId, setTpUserId] = useState("");
    const [tpAccountId, setTpAccountId] = useState("");
    const [tpDays, setTpDays] = useState(30);

    const { data: wallets = [], isLoading, refetch } = useGetBingXWalletsQuery();
    const topupArgs = useMemo(
        () => ({
            status: tpStatus === "all" ? undefined : tpStatus,
            network: tpNetwork || undefined,
            source: tpSource || undefined,
            user_id: tpUserId || undefined,
            account_id: tpAccountId || undefined,
            days: tpDays,
        }),
        [tpStatus, tpNetwork, tpSource, tpUserId, tpAccountId, tpDays]
    );
    const { data: topupsRes, refetch: refetchTopups, isFetching: topupsLoading } =
        useGetBingXTopupsQuery(topupArgs);

    const [createWallet] = useCreateBingXWalletMutation();
    const [updateWallet] = useUpdateBingXWalletMutation();
    const [deleteWallet] = useDeleteBingXWalletMutation();
    const [syncAddresses, { isLoading: syncing }] = useSyncBingXAddressesMutation();
    const [fetchBalance] = useLazyGetBingXBalanceQuery();
    const [fetchWithdrawals] = useLazyGetBingXWithdrawalsQuery();
    const [toggleAddress] = useToggleBingXAddressMutation();
    const [addAddress, { isLoading: adding }] = useAddBingXAddressMutation();
    const [resetSumTotal] = useResetBingXSumTotalMutation();

    const normalize = (w: any): BingXWallet => ({
        alny_bingx_accounts_id: w.alny_bingx_accounts_id ?? w.id ?? 0,
        alny_bingx_accounts_account: w.alny_bingx_accounts_account ?? w.account ?? "",
        alny_bingx_accounts_sum_total: Number(w.alny_bingx_accounts_sum_total ?? w.sum_total ?? 0),
        credited_total: Number(w.credited_total ?? w.alny_bingx_accounts_sum_total ?? w.sum_total ?? 0),
        sum_total_reset_at: w.sum_total_reset_at ?? null,
        shown_since_reset:
            w.shown_since_reset != null && w.shown_since_reset !== ""
                ? Number(w.shown_since_reset)
                : null,
        credited_since_reset: Number(
            w.credited_since_reset ?? w.alny_bingx_accounts_sum_total ?? w.sum_total ?? 0
        ),
        alny_bingx_accounts_trc20: String(w.alny_bingx_accounts_trc20 ?? w.trc20 ?? "").replace(/^0$/, ""),
        alny_bingx_accounts_bep20: String(w.alny_bingx_accounts_bep20 ?? w.bep20 ?? "").replace(/^0$/, ""),
        alny_bingx_accounts_ton: String(w.alny_bingx_accounts_ton ?? w.ton ?? "").replace(/^0$/, ""),
        alny_bingx_accounts_sum: Number(w.alny_bingx_accounts_sum ?? w.sum ?? 0),
        alny_bingx_accounts_user_id: Number(w.alny_bingx_accounts_user_id ?? w.user_id ?? 0),
        alny_bingx_accounts_start: Number(w.alny_bingx_accounts_start ?? w.start ?? 0),
        alny_bingx_accounts_trans: Number(w.alny_bingx_accounts_trans ?? w.trans ?? 0),
        alny_bingx_accounts_type: (w.alny_bingx_accounts_type ?? w.type ?? "") as any,
        alny_bingx_accounts_status: Number(w.alny_bingx_accounts_status ?? w.status ?? 0),
        api_key: w.api_key ?? "",
        secret_key: w.secret_key ?? "",
        has_api_keys: Boolean(w.has_api_keys),
        addresses: w.addresses ?? [],
        address_counts: w.address_counts ?? { bep20: 0, trc20: 0, ton: 0, busy: 0 },
    });

    const list = useMemo(
        () =>
            (wallets as any[])
                .map(normalize)
                .filter((w) =>
                    w.alny_bingx_accounts_account.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .sort((a, b) => a.alny_bingx_accounts_id - b.alny_bingx_accounts_id),
        [wallets, searchTerm]
    );

    const walletTotals = useMemo(() => {
        const active = list.filter((w) => w.alny_bingx_accounts_status === 0);
        return {
            accounts: list.length,
            active: active.length,
            creditedTotal: list.reduce((s, w) => s + (w.credited_total ?? 0), 0),
            liveTotal: Object.values(cardLive).reduce(
                (s, r) => s + (r.total != null && !Number.isNaN(r.total) ? r.total : 0),
                0
            ),
            liveKnown: Object.values(cardLive).filter((r) => r.total != null).length,
            bep20: list.reduce((s, w) => s + (w.address_counts?.bep20 ?? 0), 0),
            trc20: list.reduce((s, w) => s + (w.address_counts?.trc20 ?? 0), 0),
            ton: list.reduce((s, w) => s + (w.address_counts?.ton ?? 0), 0),
            busy: list.reduce((s, w) => s + (w.address_counts?.busy ?? 0), 0),
        };
    }, [list, cardLive]);

    const loadCardBalance = async (id: number) => {
        setCardLive((prev) => ({ ...prev, [id]: { ...(prev[id] ?? { total: null }), loading: true } }));
        try {
            const bal = await fetchBalance(id).unwrap();
            const total =
                bal?.total_usdt != null
                    ? Number(bal.total_usdt)
                    : bal?.fund_usdt != null || bal?.spot_usdt != null
                      ? Number(bal.fund_usdt ?? 0) + Number(bal.spot_usdt ?? 0)
                      : null;
            setCardLive((prev) => ({
                ...prev,
                [id]: {
                    total: total != null && !Number.isNaN(total) ? total : null,
                    err: bal?.error || bal?.fund_error || undefined,
                    loading: false,
                },
            }));
        } catch (e: any) {
            setCardLive((prev) => ({
                ...prev,
                [id]: {
                    total: null,
                    err: String(e?.data?.message || e?.data?.error || "ошибка API"),
                    loading: false,
                },
            }));
        }
    };

    const loadAllLiveBalances = async (opts?: { silent?: boolean }) => {
        const withKeys = list.filter((w) => w.has_api_keys || w.api_key);
        if (!withKeys.length) {
            if (!opts?.silent) showNotification("Нет кошельков с API ключами", "error");
            return;
        }
        setLiveLoadingAll(true);
        await Promise.all(withKeys.map((w) => loadCardBalance(w.alny_bingx_accounts_id)));
        setLiveLoadingAll(false);
        if (!opts?.silent) showNotification("Балансы биржи обновлены", "success");
    };

    // при завантаженні списку — одразу тягнемо live баланси (без ручного ↻)
    const autoLiveDone = useRef(false);
    useEffect(() => {
        if (isLoading || autoLiveDone.current) return;
        const withKeys = list.filter((w) => w.has_api_keys || w.api_key);
        if (!withKeys.length) return;
        autoLiveDone.current = true;
        void loadAllLiveBalances({ silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, list.length]);

    const currentWallet = useMemo(() => {
        if (!editing) return null;
        return list.find((w) => w.alny_bingx_accounts_id === editing.alny_bingx_accounts_id) ?? editing;
    }, [editing, list]);

    const openCreate = () => {
        setEditing(null);
        setForm({
            alny_bingx_accounts_account: "",
            alny_bingx_accounts_status: 0,
            api_key: "",
            secret_key: "",
        });
        setNewAddress("");
        setBingxLive({});
        setAddrNet("bep20");
        setModalTab("main");
        setModalOpen(true);
    };

    const openEdit = (w: BingXWallet, tab: ModalTab = "main") => {
        setEditing(w);
        setForm({
            alny_bingx_accounts_account: w.alny_bingx_accounts_account,
            alny_bingx_accounts_status: w.alny_bingx_accounts_status,
            api_key: w.api_key ?? "",
            secret_key: w.secret_key ?? "",
        });
        setNewAddress("");
        setBingxLive({});
        setAddrNet("bep20");
        setModalTab(tab);
        setModalOpen(true);
        if (tab === "bingx") loadBingxTab(w.alny_bingx_accounts_id);
    };

    const saveMain = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editing) {
                await updateWallet({ id: editing.alny_bingx_accounts_id, ...form }).unwrap();
                showNotification("Сохранено", "success");
                setModalOpen(false);
            } else {
                const created: any = await createWallet(form).unwrap();
                showNotification("Кошелёк создан — добавьте адреса", "success");
                const id = created?.alny_bingx_accounts_id ?? created?.id;
                const refreshed = await refetch();
                const found = (refreshed.data as any[])?.map(normalize).find(
                    (x) => x.alny_bingx_accounts_id === id
                );
                if (found) {
                    setEditing(found);
                    setModalTab("addresses");
                    return;
                }
                setModalOpen(false);
            }
            refetch();
        } catch (err: any) {
            showNotification(
                err?.data?.message ||
                    err?.data?.errors?.alny_bingx_accounts_trc20?.[0] ||
                    "Ошибка",
                "error"
            );
        }
    };

    const handleSync = async (id: number) => {
        try {
            const res = await syncAddresses(id).unwrap();
            const r = res?.result ?? {};
            const msg = res?.message || r?.message;
            if (r.error === "no_api_keys") {
                showNotification("Нет API ключей (вкладка Основное)", "error");
            } else if (r.error && r.error !== "empty") {
                showNotification(
                    `Sync: ${msg || r.error}${r.bingx_code != null ? ` [${r.bingx_code}]` : ""}`,
                    "error"
                );
            } else if (r.error === "empty") {
                showNotification(msg || "BingX вернул 0 адресов", "error");
            } else {
                showNotification(`Sync: +${r.added ?? 0} новых (всего в API ${r.total_api ?? 0})`, "success");
            }
            refetch();
        } catch (err: any) {
            showNotification(String(err?.data?.message || err?.data?.result?.message || "Ошибка Sync"), "error");
        }
    };

    const loadBingxTab = async (id: number) => {
        setBingxLive({ loading: true });
        try {
            const bal = await fetchBalance(id).unwrap();
            const wd = await fetchWithdrawals({ id, days: 30 }).unwrap();
            setBingxLive({ balance: bal, withdrawals: wd?.data ?? [], loading: false });
        } catch {
            setBingxLive({ loading: false });
            showNotification("Не удалось загрузить BingX", "error");
        }
    };

    const handleAddAddr = async () => {
        if (!currentWallet || !newAddress.trim()) return;
        try {
            await addAddress({
                id: currentWallet.alny_bingx_accounts_id,
                network: addrNet,
                address: newAddress.trim(),
            }).unwrap();
            showNotification(`Адрес ${addrNet.toUpperCase()} добавлен`, "success");
            setNewAddress("");
            refetch();
        } catch (err: any) {
            showNotification(err?.data?.message || "Не удалось добавить", "error");
        }
    };

    const withdrawTotal = useMemo(
        () => (bingxLive.withdrawals ?? []).reduce((s, r) => s + (Number(r.amount) || 0), 0),
        [bingxLive.withdrawals]
    );
    const summary = topupsRes?.summary;

    if (isLoading) {
        return <div className="p-8 text-center text-gray-500">Загрузка…</div>;
    }

    const addrList = (currentWallet?.addresses ?? []).filter((a) => a.network === addrNet);
    const netMeta = NETWORKS.find((n) => n.key === addrNet)!;

    return (
        <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-4">
            {/* fixed header — кнопки не дёргаются */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Крипто кошельки BingX
                    </h1>
                    <p className="text-sm text-gray-500">
                        Пул адресов · зачисления в Хаб (usdt_wallet)
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
                        <button
                            type="button"
                            onClick={() => setMainTab("wallets")}
                            className={`px-4 py-2 text-sm font-medium ${
                                mainTab === "wallets" ? "bg-blue-600 text-white" : "text-gray-700 dark:text-gray-200"
                            }`}
                        >
                            Кошельки
                        </button>
                        <button
                            type="button"
                            onClick={() => setMainTab("topups")}
                            className={`px-4 py-2 text-sm font-medium ${
                                mainTab === "topups" ? "bg-blue-600 text-white" : "text-gray-700 dark:text-gray-200"
                            }`}
                        >
                            Пополнения
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={openCreate}
                        className="px-4 py-2 rounded-xl text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                    >
                        + Добавить кошелёк
                    </button>
                </div>
            </div>

            {/* KPI */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {[
                    { l: "Аккаунтов", v: walletTotals.accounts },
                    { l: "Рабочих", v: walletTotals.active, c: "text-emerald-600" },
                    {
                        l: "Всего в Хаб (всё время)",
                        v: fmtUsd(walletTotals.creditedTotal),
                        tip: "Сумма всех оплаченных crypto-заявок по адресам. Не период фильтра «Пополнения».",
                    },
                    {
                        l: "На бирже сейчас",
                        v:
                            walletTotals.liveKnown > 0
                                ? fmtUsd(walletTotals.liveTotal)
                                : "нажми ↻",
                        tip: "Live BingX после «Обновить балансы».",
                    },
                    { l: "BEP20 / TRC20", v: `${walletTotals.bep20} / ${walletTotals.trc20}` },
                    { l: "Занято сейчас", v: walletTotals.busy, c: walletTotals.busy ? "text-orange-600" : "" },
                ].map((c) => (
                    <div
                        key={c.l}
                        title={(c as any).tip}
                        className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
                    >
                        <div className="text-[10px] uppercase tracking-wide text-gray-500">{c.l}</div>
                        <div className={`text-base font-bold mt-0.5 ${c.c ?? ""}`}>{c.v}</div>
                    </div>
                ))}
            </div>

            {/* ===== WALLETS (cards) ===== */}
            {mainTab === "wallets" && (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Поиск по email…"
                            className="w-full max-w-xs px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
                        />
                        <button
                            type="button"
                            onClick={() => loadAllLiveBalances()}
                            disabled={liveLoadingAll}
                            className="px-3 py-2 rounded-xl border text-sm font-medium bg-white dark:bg-gray-800"
                        >
                            {liveLoadingAll ? "Балансы…" : "↻ Балансы биржи"}
                        </button>
                        <span className="text-xs text-gray-500">
                            «Заявлено» = що юзер вписав; без Сброса = за всё время. Сброс — новая
                            точка после вывода ≈0.
                        </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {list.map((w) => {
                            const live = cardLive[w.alny_bingx_accounts_id];
                            const shown = w.shown_since_reset;
                            const credited = w.credited_since_reset ?? w.alny_bingx_accounts_sum_total;
                            const hasReset = !!w.sum_total_reset_at;
                            const liveVal = live?.total;
                            const gap =
                                liveVal != null && shown != null ? liveVal - shown : null;
                            return (
                            <div
                                key={w.alny_bingx_accounts_id}
                                className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm flex flex-col"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div
                                            className="font-semibold text-sm truncate max-w-[200px]"
                                            title={w.alny_bingx_accounts_account}
                                        >
                                            {w.alny_bingx_accounts_account}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            #{w.alny_bingx_accounts_id}
                                            {" · "}
                                            {w.has_api_keys || w.api_key ? (
                                                <span className="text-emerald-600">API ✓</span>
                                            ) : (
                                                <span className="text-red-500">нет API</span>
                                            )}
                                            {hasReset && (
                                                <span className="text-gray-400">
                                                    {" · "}сброс{" "}
                                                    {new Date(
                                                        (w.sum_total_reset_at as number) * 1000
                                                    ).toLocaleDateString("ru-RU")}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {w.alny_bingx_accounts_status === 0 ? (
                                        <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                                            Рабочий
                                        </span>
                                    ) : (
                                        <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-800">
                                            Off
                                        </span>
                                    )}
                                </div>

                                <div className="mt-3 grid grid-cols-2 gap-2">
                                    <div
                                        className="rounded-xl bg-amber-50 dark:bg-amber-950/30 px-2.5 py-2"
                                        title="Сумма, которую юзер сам вписал в поле пополнения (может завысить)"
                                    >
                                        <div className="text-[10px] uppercase text-amber-900 leading-tight">
                                            Заявлено
                                            <span className="block font-normal normal-case text-amber-800/70">
                                                вписал юзер · {hasReset ? "с сброса" : "всё время"}
                                            </span>
                                        </div>
                                        <div className="text-lg font-bold tabular-nums mt-0.5">
                                            {fmtUsd(shown ?? 0)}
                                        </div>
                                        <div className="text-[10px] text-gray-500 mt-0.5">
                                            факт в Хаб: {fmtUsd(credited ?? 0)}
                                        </div>
                                    </div>
                                    <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 px-2.5 py-2">
                                        <div className="flex items-center justify-between gap-1">
                                            <div className="text-[10px] uppercase text-emerald-800 leading-tight">
                                                На бирже
                                                <span className="block font-normal normal-case text-emerald-700/70">
                                                    сейчас
                                                </span>
                                            </div>
                                            {(w.has_api_keys || w.api_key) && (
                                                <button
                                                    type="button"
                                                    className="text-xs px-1.5 py-0.5 rounded border border-emerald-200"
                                                    title="Обновить live баланс"
                                                    onClick={() =>
                                                        loadCardBalance(w.alny_bingx_accounts_id)
                                                    }
                                                >
                                                    {live?.loading ? "…" : "↻"}
                                                </button>
                                            )}
                                        </div>
                                        <div className="text-lg font-bold tabular-nums mt-0.5">
                                            {live?.loading
                                                ? "…"
                                                : liveVal != null
                                                  ? fmtUsd(liveVal)
                                                  : w.has_api_keys || w.api_key
                                                    ? "—"
                                                    : "нет API"}
                                        </div>
                                        {gap != null && Math.abs(gap) >= 0.01 && (
                                            <div
                                                className={`text-[10px] font-semibold mt-0.5 ${
                                                    gap < -0.5 ? "text-red-600" : "text-gray-500"
                                                }`}
                                                title="биржа − заявлено (если не выводили — при недоплате Δ красная)"
                                            >
                                                Δ {fmtUsd(gap)}
                                            </div>
                                        )}
                                        {live?.err && (
                                            <div
                                                className="text-[10px] text-red-600 truncate"
                                                title={live.err}
                                            >
                                                {live.err}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {NETWORKS.map((n) => (
                                        <button
                                            key={n.key}
                                            type="button"
                                            onClick={() => {
                                                setAddrNet(n.key);
                                                openEdit(w, "addresses");
                                            }}
                                            className={`text-[11px] font-semibold px-2 py-1 rounded-lg ${n.chip}`}
                                        >
                                            {n.label} {w.address_counts?.[n.key] ?? 0}
                                        </button>
                                    ))}
                                    {(w.address_counts?.busy ?? 0) > 0 && (
                                        <span className="text-[11px] px-2 py-1 rounded-lg bg-orange-100 text-orange-800 font-medium">
                                            занято {w.address_counts?.busy}
                                        </span>
                                    )}
                                </div>

                                <div className="mt-auto pt-4 flex flex-wrap gap-1.5">
                                    <button
                                        type="button"
                                        className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white"
                                        onClick={() => openEdit(w, "main")}
                                    >
                                        Открыть
                                    </button>
                                    <button
                                        type="button"
                                        className="text-xs px-3 py-1.5 rounded-lg border"
                                        onClick={() => openEdit(w, "bingx")}
                                    >
                                        Баланс BingX
                                    </button>
                                    <button
                                        type="button"
                                        className="text-xs px-3 py-1.5 rounded-lg border text-red-600 border-red-200"
                                        title="После вывода с биржи до ~0: обнуляет период сравнения"
                                        onClick={async () => {
                                            if (
                                                !window.confirm(
                                                    `Сброс периода для #${w.alny_bingx_accounts_id}?\n\nПосле вывода с BingX до ~0.\nДальше: «Заявлено» (вписал юзер) vs «На бирже» (факт).\nСтарый sum_total=${w.alny_bingx_accounts_sum_total}`
                                                )
                                            )
                                                return;
                                            await resetSumTotal(w.alny_bingx_accounts_id).unwrap();
                                            showNotification("Период сброса начат", "success");
                                            refetch();
                                        }}
                                    >
                                        Сброс периода
                                    </button>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                    {list.length === 0 && (
                        <div className="rounded-2xl border border-dashed p-10 text-center text-gray-500">
                            Кошельков нет
                        </div>
                    )}
                </div>
            )}

            {/* ===== TOPUPS ===== */}
            {mainTab === "topups" && (
                <div className="space-y-3">
                    <div className="rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                        Цифры здесь — <b>только за выбранные дни</b> (фильтр ниже). На карточках
                        «Всего в Хаб» — <b>за всё время</b>. Поэтому суммы часто не совпадают.
                        Колонка «Зачислено $» = факт на <code>usdt_wallet</code>.
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        {[
                            { l: "В выборке", v: summary?.count ?? 0 },
                            { l: "Оплачено", v: summary?.paid_count ?? 0, c: "text-emerald-600" },
                            { l: "Ожидает", v: summary?.pending_count ?? 0, c: "text-amber-600" },
                            { l: "Зачислено $", v: fmtUsd(summary?.received_total) },
                            {
                                l: "Недоплаты",
                                v: summary?.underpaid_count ?? 0,
                                c: (summary?.underpaid_count ?? 0) > 0 ? "text-red-600" : "",
                            },
                        ].map((c) => (
                            <div
                                key={c.l}
                                className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-2"
                            >
                                <div className="text-[10px] text-gray-500 uppercase">{c.l}</div>
                                <div className={`text-lg font-bold ${c.c ?? ""}`}>{c.v}</div>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-wrap gap-2 items-end bg-white dark:bg-gray-800 border rounded-xl p-3">
                        <label className="text-xs">
                            Статус
                            <select
                                className="block mt-1 px-2 py-1.5 rounded-lg border text-sm dark:bg-gray-900"
                                value={tpStatus}
                                onChange={(e) => setTpStatus(e.target.value)}
                            >
                                <option value="all">Все</option>
                                <option value="pending">Ожидает</option>
                                <option value="paid">Оплачен</option>
                                <option value="cancelled">Отменён</option>
                            </select>
                        </label>
                        <label className="text-xs">
                            Источник
                            <select
                                className="block mt-1 px-2 py-1.5 rounded-lg border text-sm dark:bg-gray-900"
                                value={tpSource}
                                onChange={(e) => setTpSource(e.target.value)}
                            >
                                <option value="">Все</option>
                                <option value="hub">Хаб</option>
                                <option value="millennium">Millennium</option>
                            </select>
                        </label>
                        <label className="text-xs">
                            Сеть
                            <select
                                className="block mt-1 px-2 py-1.5 rounded-lg border text-sm dark:bg-gray-900"
                                value={tpNetwork}
                                onChange={(e) => setTpNetwork(e.target.value)}
                            >
                                <option value="">Все</option>
                                <option value="bep20">BEP20</option>
                                <option value="trc20">TRC20</option>
                                <option value="ton">TON</option>
                            </select>
                        </label>
                        <label className="text-xs">
                            Кошелёк
                            <select
                                className="block mt-1 px-2 py-1.5 rounded-lg border text-sm dark:bg-gray-900 max-w-[200px]"
                                value={tpAccountId}
                                onChange={(e) => setTpAccountId(e.target.value)}
                            >
                                <option value="">Все</option>
                                {list.map((w) => (
                                    <option key={w.alny_bingx_accounts_id} value={w.alny_bingx_accounts_id}>
                                        #{w.alny_bingx_accounts_id} {w.alny_bingx_accounts_account}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="text-xs">
                            User ID
                            <input
                                className="block mt-1 px-2 py-1.5 rounded-lg border text-sm dark:bg-gray-900 w-24"
                                value={tpUserId}
                                onChange={(e) => setTpUserId(e.target.value)}
                            />
                        </label>
                        <label className="text-xs">
                            Дней
                            <select
                                className="block mt-1 px-2 py-1.5 rounded-lg border text-sm dark:bg-gray-900"
                                value={tpDays}
                                onChange={(e) => setTpDays(Number(e.target.value))}
                            >
                                {[7, 14, 30, 60, 90].map((d) => (
                                    <option key={d} value={d}>
                                        {d}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <button
                            type="button"
                            className="px-3 py-1.5 rounded-lg border text-sm"
                            onClick={() => refetchTopups()}
                        >
                            {topupsLoading ? "…" : "Обновить"}
                        </button>
                    </div>

                    <div className="rounded-2xl border bg-white dark:bg-gray-800 overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-900 text-left text-[11px] uppercase text-gray-500">
                                <tr>
                                    <th className="px-2 py-1.5">ID</th>
                                    <th className="px-2 py-1.5">Пользователь</th>
                                    <th className="px-2 py-1.5">Сеть</th>
                                    <th className="px-2 py-1.5">Заявлено $</th>
                                    <th className="px-2 py-1.5">Зачислено $</th>
                                    <th className="px-2 py-1.5 w-[1%] whitespace-nowrap">Источник</th>
                                    <th className="px-2 py-1.5">Статус</th>
                                    <th className="px-2 py-1.5">Адрес</th>
                                    <th className="px-2 py-1.5">Время</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y dark:divide-gray-700">
                                {(topupsRes?.data ?? []).map((r: any) => {
                                    const under = r.diff != null && r.diff < -0.0001;
                                    return (
                                        <tr key={r.id} className={under ? "bg-red-50/50" : ""}>
                                            <td className="px-2 py-1.5 tabular-nums">{r.id}</td>
                                            <td className="px-2 py-1.5">
                                                <div
                                                    className="font-medium truncate max-w-[220px]"
                                                    title={r.user_name || ""}
                                                >
                                                    {r.user_name || "—"}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    id {r.user_id}
                                                    {r.user_secret ? ` · ${r.user_secret}` : ""}
                                                </div>
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <span
                                                    className={`text-[11px] font-bold px-2 py-0.5 rounded-md uppercase ${
                                                        NETWORKS.find((n) => n.key === r.network)?.chip ??
                                                        "bg-gray-100"
                                                    }`}
                                                >
                                                    {r.network}
                                                </span>
                                            </td>
                                            <td className="px-2 py-1.5 tabular-nums font-semibold whitespace-nowrap">
                                                {r.requested != null ? (
                                                    fmtUsd(r.requested, 4)
                                                ) : (
                                                    <span className="text-gray-400 text-xs">нет данных</span>
                                                )}
                                            </td>
                                            <td className="px-2 py-1.5 tabular-nums font-semibold text-emerald-700 whitespace-nowrap">
                                                {r.received != null ? fmtUsd(r.received, 4) : "—"}
                                            </td>
                                            <td className="px-2 py-1.5 w-[1%]">
                                                <span title={r.order_id || undefined}>
                                                    <SourceBadge source={r.source} />
                                                </span>
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <StatusBadge status={r.status} />
                                            </td>
                                            <td
                                                className="px-2 py-1.5 font-mono text-xs max-w-[120px] truncate"
                                                title={r.address}
                                            >
                                                {shortAddr(r.address)}
                                            </td>
                                            <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-500">
                                                {r.time
                                                    ? new Date(r.time * 1000).toLocaleString("ru-RU")
                                                    : "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {(topupsRes?.data ?? []).length === 0 && (
                            <div className="p-8 text-center text-gray-500">Нет записей</div>
                        )}
                    </div>
                </div>
            )}

            {/* ===== MODAL ===== */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="px-5 py-3 border-b flex justify-between items-center gap-2">
                            <div className="min-w-0">
                                <h2 className="font-semibold">
                                    {editing ? "Кошелёк" : "Новый кошелёк"}
                                </h2>
                                {editing && (
                                    <p
                                        className="text-xs text-gray-500 truncate max-w-[200px]"
                                        title={editing.alny_bingx_accounts_account}
                                    >
                                        #{editing.alny_bingx_accounts_id} ·{" "}
                                        {editing.alny_bingx_accounts_account}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                className="text-2xl leading-none text-gray-400 hover:text-gray-700 px-1"
                                onClick={() => setModalOpen(false)}
                            >
                                ×
                            </button>
                        </div>

                        {editing && (
                            <div className="px-5 flex gap-1 border-b bg-gray-50/80 dark:bg-gray-900/40">
                                {(
                                    [
                                        ["main", "Основное"],
                                        ["addresses", "Адреса"],
                                        ["bingx", "Баланс BingX"],
                                    ] as [ModalTab, string][]
                                ).map(([k, label]) => (
                                    <button
                                        key={k}
                                        type="button"
                                        onClick={() => {
                                            setModalTab(k);
                                            if (k === "bingx") {
                                                loadBingxTab(editing.alny_bingx_accounts_id);
                                            }
                                        }}
                                        className={`px-3 py-2.5 text-sm ${
                                            modalTab === k
                                                ? "font-semibold text-blue-700 border-b-2 border-blue-600"
                                                : "text-gray-500"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="p-5 overflow-y-auto flex-1">
                            {(modalTab === "main" || !editing) && (
                                <form onSubmit={saveMain} className="space-y-3 max-w-md">
                                    <label className="block text-sm font-medium">
                                        Email BingX
                                        <input
                                            required
                                            type="email"
                                            className="mt-1 w-full border rounded-xl px-3 py-2 dark:bg-gray-900"
                                            value={form.alny_bingx_accounts_account}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    alny_bingx_accounts_account: e.target.value,
                                                })
                                            }
                                        />
                                    </label>
                                    <label className="block text-sm font-medium">
                                        API Key
                                        <input
                                            className="mt-1 w-full border rounded-xl px-3 py-2 font-mono text-xs dark:bg-gray-900"
                                            value={form.api_key}
                                            onChange={(e) =>
                                                setForm({ ...form, api_key: e.target.value })
                                            }
                                        />
                                    </label>
                                    <label className="block text-sm font-medium">
                                        Secret Key
                                        <input
                                            className="mt-1 w-full border rounded-xl px-3 py-2 font-mono text-xs dark:bg-gray-900"
                                            value={form.secret_key}
                                            onChange={(e) =>
                                                setForm({ ...form, secret_key: e.target.value })
                                            }
                                        />
                                    </label>
                                    <label className="block text-sm font-medium">
                                        Статус
                                        <select
                                            className="mt-1 w-full border rounded-xl px-3 py-2 dark:bg-gray-900"
                                            value={form.alny_bingx_accounts_status}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    alny_bingx_accounts_status: Number(e.target.value),
                                                })
                                            }
                                        >
                                            <option value={0}>Рабочий</option>
                                            <option value={1}>Отключён</option>
                                        </select>
                                    </label>
                                    <div className="flex gap-2 pt-1">
                                        <button
                                            type="submit"
                                            className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium"
                                        >
                                            Сохранить
                                        </button>
                                        {editing && (
                                            <button
                                                type="button"
                                                className="px-4 py-2 rounded-xl border text-red-600 text-sm ml-auto"
                                                onClick={async () => {
                                                    if (!window.confirm("Удалить кошелёк?")) return;
                                                    await deleteWallet(
                                                        editing.alny_bingx_accounts_id
                                                    ).unwrap();
                                                    setModalOpen(false);
                                                    refetch();
                                                }}
                                            >
                                                Удалить
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        Адреса и Sync — на вкладке «Адреса». Баланс биржи — «Баланс BingX».
                                    </p>
                                </form>
                            )}

                            {modalTab === "addresses" && currentWallet && (
                                <div className="space-y-4">
                                    {/* одна кнопка Sync */}
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <p className="text-sm text-gray-600 dark:text-gray-300">
                                            Выберите сеть → список адресов → добавьте вручную или Sync
                                        </p>
                                        <button
                                            type="button"
                                            disabled={syncing}
                                            className="text-sm px-3 py-2 rounded-xl bg-indigo-600 text-white disabled:opacity-50 shrink-0"
                                            onClick={() =>
                                                handleSync(currentWallet.alny_bingx_accounts_id)
                                            }
                                        >
                                            {syncing ? "Sync…" : "Sync с BingX"}
                                        </button>
                                    </div>

                                    {/* переключатель сетей */}
                                    <div className="flex rounded-xl border overflow-hidden">
                                        {NETWORKS.map((n) => {
                                            const cnt = (currentWallet.addresses ?? []).filter(
                                                (a) => a.network === n.key && a.status === 0
                                            ).length;
                                            return (
                                                <button
                                                    key={n.key}
                                                    type="button"
                                                    onClick={() => {
                                                        setAddrNet(n.key);
                                                        setNewAddress("");
                                                    }}
                                                    className={`flex-1 py-2.5 text-sm font-semibold ${
                                                        addrNet === n.key
                                                            ? `${n.chip} ring-inset`
                                                            : "bg-white dark:bg-gray-800 text-gray-500"
                                                    }`}
                                                >
                                                    {n.label}
                                                    <span className="ml-1 opacity-70">({cnt})</span>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className={`h-1 rounded-full ${netMeta.bar}`} />

                                    {/* список */}
                                    <div className="space-y-2 max-h-56 overflow-y-auto">
                                        {addrList.length === 0 && (
                                            <div className="text-sm text-gray-500 py-4 text-center border border-dashed rounded-xl">
                                                Нет адресов {netMeta.label}. Добавьте ниже или Sync.
                                            </div>
                                        )}
                                        {addrList.map((a: BingXAddress) => (
                                            <div
                                                key={a.id}
                                                className={`rounded-xl border bg-gray-50 dark:bg-gray-900/50 px-3 py-2 ${
                                                    a.status === 1 ? "opacity-50" : ""
                                                }`}
                                            >
                                                <div className="font-mono text-xs break-all leading-relaxed">
                                                    {a.address}
                                                </div>
                                                <div className="flex flex-wrap gap-2 mt-1.5 items-center text-[11px]">
                                                    <span
                                                        className={`font-semibold px-1.5 py-0.5 rounded ${
                                                            a.status === 1
                                                                ? "bg-gray-200 text-gray-600"
                                                                : a.busy
                                                                  ? "bg-orange-100 text-orange-800"
                                                                  : "bg-emerald-100 text-emerald-800"
                                                        }`}
                                                    >
                                                        {a.status === 1
                                                            ? "выкл"
                                                            : a.busy
                                                              ? `занят · user ${a.busy_user_id}`
                                                              : "свободен"}
                                                    </span>
                                                    <span className="text-gray-400">
                                                        {a.source === "sync" ? "sync" : "ручной"}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="text-blue-600 ml-auto"
                                                        onClick={() =>
                                                            navigator.clipboard.writeText(a.address)
                                                        }
                                                    >
                                                        копир.
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="text-gray-600"
                                                        onClick={async () => {
                                                            await toggleAddress({
                                                                id: currentWallet.alny_bingx_accounts_id,
                                                                address_id: a.id,
                                                                status: a.status === 0 ? 1 : 0,
                                                            }).unwrap();
                                                            refetch();
                                                        }}
                                                    >
                                                        {a.status === 0 ? "выкл" : "вкл"}
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* добавить */}
                                    <div className="rounded-xl border border-dashed border-gray-300 p-3 space-y-2">
                                        <div className="text-xs font-medium text-gray-600">
                                            Добавить адрес {netMeta.label}
                                        </div>
                                        <div className="flex gap-2">
                                            <input
                                                className="flex-1 border rounded-xl px-3 py-2 font-mono text-xs dark:bg-gray-900"
                                                placeholder={
                                                    addrNet === "bep20"
                                                        ? "0x…"
                                                        : addrNet === "trc20"
                                                          ? "T…"
                                                          : "UQ… / EQ…"
                                                }
                                                value={newAddress}
                                                onChange={(e) => setNewAddress(e.target.value)}
                                            />
                                            <button
                                                type="button"
                                                disabled={adding || !newAddress.trim()}
                                                className="px-3 py-2 rounded-xl bg-blue-600 text-white text-sm disabled:opacity-40 shrink-0"
                                                onClick={handleAddAddr}
                                            >
                                                Добавить
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {modalTab === "bingx" && editing && (
                                <div className="space-y-4">
                                    {bingxLive.loading && (
                                        <div className="text-sm text-gray-500">Загрузка…</div>
                                    )}
                                    <p className="text-xs text-slate-500">
                                        Live с биржи. «Всего в Хаб» на карточке — сумма оплаченных
                                        заявок за всё время, не баланс BingX.
                                    </p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                        <div className="rounded-xl bg-indigo-50 dark:bg-indigo-950/40 p-3">
                                            <div className="text-[10px] text-indigo-700 uppercase">
                                                Всего ≈
                                            </div>
                                            <div className="text-xl font-bold">
                                                {fmtUsd(bingxLive.balance?.total_usdt)}
                                            </div>
                                        </div>
                                        <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/40 p-3">
                                            <div className="text-[10px] text-emerald-700 uppercase">
                                                Fund
                                            </div>
                                            <div className="text-xl font-bold">
                                                {fmtUsd(bingxLive.balance?.fund_usdt)}
                                            </div>
                                        </div>
                                        <div className="rounded-xl bg-sky-50 dark:bg-sky-950/40 p-3">
                                            <div className="text-[10px] text-sky-700 uppercase">
                                                Spot
                                            </div>
                                            <div className="text-xl font-bold">
                                                {fmtUsd(bingxLive.balance?.spot_usdt)}
                                            </div>
                                        </div>
                                        <div className="rounded-xl bg-violet-50 dark:bg-violet-950/40 p-3">
                                            <div className="text-[10px] text-violet-700 uppercase">
                                                Выводы 30д
                                            </div>
                                            <div className="text-xl font-bold">
                                                {fmtUsd(withdrawTotal)}
                                            </div>
                                        </div>
                                    </div>
                                    {(bingxLive.balance?.error ||
                                        bingxLive.balance?.fund_error ||
                                        bingxLive.balance?.spot_error) && (
                                        <div className="text-sm text-red-600 space-y-1">
                                            {bingxLive.balance?.error === "no_api_keys" ? (
                                                <div>Нет API ключей</div>
                                            ) : (
                                                <>
                                                    {bingxLive.balance?.error && (
                                                        <div>{String(bingxLive.balance.error)}</div>
                                                    )}
                                                    {bingxLive.balance?.fund_error && (
                                                        <div>
                                                            Fund: {String(bingxLive.balance.fund_error)}
                                                        </div>
                                                    )}
                                                    {bingxLive.balance?.spot_error && (
                                                        <div>
                                                            Spot: {String(bingxLive.balance.spot_error)}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {(bingxLive.balance?.accounts ?? []).length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 text-[11px]">
                                            {(bingxLive.balance.accounts as any[]).map((a, i) => (
                                                <span
                                                    key={i}
                                                    className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-900"
                                                >
                                                    {a.accountType || "?"}: {fmtUsd(a.usdt)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="overflow-x-auto max-h-56 rounded-xl border">
                                        <table className="min-w-full text-xs">
                                            <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                                                <tr className="text-left text-gray-500">
                                                    <th className="px-3 py-2">Сумма</th>
                                                    <th className="px-3 py-2">Адрес</th>
                                                    <th className="px-3 py-2">Статус</th>
                                                    <th className="px-3 py-2">TX</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(bingxLive.withdrawals ?? []).map(
                                                    (r: any, i: number) => (
                                                        <tr key={i} className="border-t">
                                                            <td className="px-3 py-1.5 font-semibold">
                                                                {r.amount}
                                                            </td>
                                                            <td
                                                                className="px-3 py-1.5 font-mono"
                                                                title={r.address}
                                                            >
                                                                {shortAddr(r.address)}
                                                            </td>
                                                            <td className="px-3 py-1.5">
                                                                {String(r.status)}
                                                            </td>
                                                            <td
                                                                className="px-3 py-1.5 font-mono"
                                                                title={r.txId}
                                                            >
                                                                {shortAddr(r.txId)}
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                        {(bingxLive.withdrawals ?? []).length === 0 &&
                                            !bingxLive.loading && (
                                                <div className="p-4 text-gray-500 text-sm">
                                                    Нет выводов
                                                </div>
                                            )}
                                    </div>
                                    <button
                                        type="button"
                                        className="text-sm px-3 py-2 rounded-xl border"
                                        onClick={() => loadBingxTab(editing.alny_bingx_accounts_id)}
                                    >
                                        Обновить
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CryptoWalletsPage;
